import { useState } from 'react';
import { Loader2, Download, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { smoobuAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function SmoobuBackfillPanel() {
  const [daysBack, setDaysBack] = useState(365);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runBackfill = async () => {
    const value = Math.max(1, Math.min(730, Number(daysBack) || 365));
    setDaysBack(value);
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await smoobuAPI.syncReservations({ days_back: value });
      setResult(data);
      toast.success(`Smoobu backfill complete: ${data.synced ?? 0} fetched, ${data.created ?? 0} created`);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Smoobu Backfill</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Use 365 to backfill the current calendar year. Larger values take longer.
          Safe to run multiple times; bookings are upserted, not duplicated.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Days back
            </label>
            <input
              type="number"
              value={daysBack}
              onChange={(e) => setDaysBack(e.target.value)}
              min={1}
              max={730}
              disabled={loading}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-28 tabular-nums"
            />
          </div>
          <Button onClick={runBackfill} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Run Smoobu Backfill
          </Button>
        </div>

        {error && (
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        )}

        {result && (
          <div className="rounded-lg border p-3 bg-muted/30 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Backfill complete</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Effective days back</p>
                <p className="text-sm font-medium tabular-nums">{result.effective_days_back ?? '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reservations fetched</p>
                <p className="text-sm font-medium tabular-nums">{result.synced ?? '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-sm font-medium tabular-nums">{result.created ?? '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Updated</p>
                <p className="text-sm font-medium tabular-nums">{result.updated ?? '-'}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
